// ============================================================================
// DraftReportCard — the internal grade that rides a full proposal draft.
// ----------------------------------------------------------------------------
// docs/pq_draft_proposal_spec.md §8. This component RENDERS ONLY. Every
// verdict, count and gap on it was computed server-side in pq-chat
// (buildReportCard) from the contractor's own rows. Same governing rule as the
// confidence badges: the server grades, the model never writes it. A report
// card the model authors is flattery with a number attached, and the spec is
// explicit that it would be worse than no report card at all.
//
// Design rules:
//
//  - THE GAPS OUTRANK THE GRADES. "You confirmed rotted sill plate on the
//    walkthrough and it is not in this proposal" is the line that saves a
//    change order. Verdict pills are a glance; the gap list is the product.
//    So gaps surface in the collapsed state, grades summarise.
//  - VERDICTS LOOK NOTHING LIKE BASIS BADGES. Those are per-line letters
//    (A/B/C/Y) for where a price came from; these are whole-document
//    judgements. Same palette would read as the same system and invite
//    misreading, so these are dots and words, never letters in a box.
//  - WHAT IT CANNOT GRADE IS PART OF THE CARD, not a footnote. Bid-vs-actual
//    needs closed jobs the business has not finished yet. Showing "3 of ~30"
//    is honest, and it makes the runway to LGI legible from inside the app.
// ============================================================================
import React, { useState } from 'react';
import { ChevronDown, ChevronUp, AlertTriangle } from 'lucide-react';
import { cn } from '@/lib/utils';
import { PQShield } from '@/components/pq/PQMark';

// Whole-document verdicts. Deliberately dots + words, not lettered chips —
// see the design rule above.
const VERDICTS = {
  strong: { dot: 'bg-green-500', text: 'text-green-600', label: 'solid' },
  mixed: { dot: 'bg-amber-500', text: 'text-amber-600', label: 'mixed' },
  weak: { dot: 'bg-orange-500', text: 'text-orange-600', label: 'needs work' },
  none: { dot: 'bg-muted-foreground/50', text: 'text-muted-foreground', label: 'nothing to grade' },
  // A transfer of the contractor's own finished proposal — his figures,
  // neither good nor bad comps.
  copied: { dot: 'bg-sky-500', text: 'text-sky-600', label: 'from your proposal' },
};

const GAP_TYPES = {
  missed: 'Missed',
  complication: 'Complication',
  scope: 'Scope',
  verify: 'Verify',
};

const VerdictPill = ({ grade }) => {
  const v = VERDICTS[grade?.verdict] || VERDICTS.none;
  return (
    <span className="inline-flex items-center gap-1 text-[10px]">
      <span className={cn('h-1.5 w-1.5 rounded-full shrink-0', v.dot)} aria-hidden="true" />
      <span className="text-muted-foreground">{grade.label}</span>
      <span className={cn('font-medium', v.text)}>{v.label}</span>
    </span>
  );
};

const DraftReportCard = ({ report, t }) => {
  const [open, setOpen] = useState(false);
  if (!report) return null;

  const grades = Array.isArray(report.grades) ? report.grades : [];
  const gaps = Array.isArray(report.gaps) ? report.gaps : [];
  const pending = Array.isArray(report.pending) ? report.pending : [];
  if (grades.length === 0 && gaps.length === 0) return null;

  return (
    <div
      className="rounded border border-border/60 bg-[hsl(var(--card))] px-2 py-1.5 space-y-1.5"
      data-testid="pq-draft-report"
    >
      <button
        type="button"
        onClick={() => setOpen((v) => !v)}
        aria-expanded={open}
        className="flex w-full items-center gap-1.5 text-left"
        data-testid="pq-draft-report-toggle"
      >
        <PQShield size={14} />
        <span className="text-[10px] uppercase tracking-[0.14em] text-primary">
          {t('pqchat.report_label', { defaultValue: 'Report card' })}
        </span>
        <span className="ml-auto shrink-0 text-muted-foreground">
          {open ? <ChevronUp size={12} /> : <ChevronDown size={12} />}
        </span>
      </button>

      {/* Glance state: one pill per graded signal. */}
      <div className="flex flex-wrap items-center gap-x-3 gap-y-1">
        {grades.map((g) => (
          <VerdictPill key={g.id} grade={g} />
        ))}
      </div>

      {/* Gaps ride the collapsed state — they are the reason this exists. */}
      {gaps.length > 0 && !open ? (
        <div className="flex items-start gap-1.5 text-[11px] text-orange-600">
          <AlertTriangle size={12} className="mt-0.5 shrink-0" />
          <span>
            {t('pqchat.report_gaps_teaser', {
              defaultValue:
                "{{count}} thing(s) you confirmed on the walkthrough aren't covered in this proposal yet — tap to read them.",
              count: gaps.length,
            })}
          </span>
        </div>
      ) : null}

      {open ? (
        <div className="space-y-2 pt-0.5">
          {grades.map((g) => (
            <div key={g.id}>
              <div className="text-[11px] font-medium text-foreground">{g.label}</div>
              {(Array.isArray(g.detail) ? g.detail : []).map((d, i) => (
                <div key={i} className="text-[11px] text-muted-foreground">
                  {d}
                </div>
              ))}
            </div>
          ))}

          {gaps.length > 0 ? (
            <div>
              <div className="flex items-center gap-1 text-[11px] font-medium text-orange-600">
                <AlertTriangle size={12} className="shrink-0" />
                {t('pqchat.report_gaps', {
                  defaultValue: 'Confirmed on site, not covered yet',
                })}
              </div>
              <ul className="mt-0.5 space-y-1">
                {gaps.map((g, i) => (
                  <li key={i} className="text-[11px] text-muted-foreground">
                    <span className="text-foreground">
                      {GAP_TYPES[g.type] || 'Finding'}:
                    </span>{' '}
                    {g.text}
                    {g.expect ? (
                      <span className="italic opacity-80"> — {g.expect}</span>
                    ) : null}
                  </li>
                ))}
              </ul>
              {report.gaps_more > 0 ? (
                <div className="mt-0.5 text-[11px] text-muted-foreground">
                  {t('pqchat.report_gaps_more', {
                    defaultValue: '+{{count}} more not shown.',
                    count: report.gaps_more,
                  })}
                </div>
              ) : null}
            </div>
          ) : null}

          {pending.length > 0 ? (
            <div>
              <div className="text-[11px] font-medium text-muted-foreground">
                {t('pqchat.report_pending', {
                  defaultValue: "Can't grade this yet",
                })}
              </div>
              {pending.map((p, i) => (
                <div key={i} className="mt-0.5 text-[11px] text-muted-foreground">
                  <span className="text-foreground">{p.label}</span>
                  {p.need > 0 ? (
                    <span>
                      {' '}
                      — {p.have} of about {p.need} closed jobs so far.
                    </span>
                  ) : null}
                  <div className="opacity-80">{p.why}</div>
                </div>
              ))}
            </div>
          ) : null}

          <div className="text-[10px] text-muted-foreground/70">
            {t('pqchat.report_footer', {
              defaultValue:
                'Computed from your own records, not written by PQ. Gap matching is by wording — check before trusting a miss.',
            })}
          </div>
        </div>
      ) : null}
    </div>
  );
};

export default DraftReportCard;
